'use client';

import React from 'react';
import {
  ITypographyAnimation,
  TTypographyColor,
  TTypographySize,
  TTypographyTag,
  Typography,
} from '@/components/atoms/Typography/Typography';
import { TwoColContainer } from '@/components/atoms/TwoColContainer/TwoColContainer';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import {
  IAnimation,
  ICommonProps,
  IScrollAnimation,
  TLink,
  TTextMultilanguage,
} from '@/types/global';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import {
  IProjectsCounterProps,
  ProjectsCounter,
} from '../ProjectsCounter/ProjectsCounter';
import * as Styled from './BannerText.styled';

gsap.registerPlugin(ScrollTrigger, SplitText);

export type IBannerTextAnimation =
  | 'firstRevealUpSecondFadeIn'
  | 'default'
  | 'none';

export interface IBannerText extends ICommonProps {
  sideText?: string | TTextMultilanguage;
  sideTextTag?: TTypographyTag;
  colorSideText?: TTypographyColor;
  titleLg?: string | TTextMultilanguage;
  title?: string | TTextMultilanguage;
  subtitle?: string | TTextMultilanguage;
  textLg?: string | TTextMultilanguage;
  text?: string | TTextMultilanguage;
  cta?: TLink;
  animationContainer?: IAnimation;
  animations?: {
    titleLg?: ITypographyAnimation;
    title?: ITypographyAnimation;
    subtitle?: ITypographyAnimation;
    textLg?: ITypographyAnimation;
    text?: ITypographyAnimation;
  };
  scrollAnimation?: IScrollAnimation;
  textAnimation?: IBannerTextAnimation;
  isContentTwoColumns?: boolean;
  borderTop?: boolean;
  sizeTitleMobile?: TTypographySize;
  sizeSubtitleMobile?: TTypographySize;
  children?: React.ReactNode;
  projectsCounter?: IProjectsCounterProps;
}

export const BannerText: React.FC<IBannerText> = ({
  sideText,
  sideTextTag = 'h2',
  titleLg,
  title,
  subtitle,
  textLg,
  text,
  cta,
  isContentTwoColumns = false,
  marginBottom = 'default',
  marginBottomOnMobile = 'default',
  marginTopPage = 'none',
  width = 'main',
  isProject = false,
  borderTop = true,
  animations,
  animationContainer = {
    type: 'fadeIn',
    duration: 0.3,
    initialOpacity: 0,
    threshold: 5,
  },
  scrollAnimation = {
    type: 'none',
  },
  // textAnimation,
  scrollSpeed = 1,
  sizeTitleMobile,
  colorSideText,
  sizeSubtitleMobile,
  projectsCounter,
  className
  // children
}) => {
  const getTextContainerWidth = () => {
    return animations?.subtitle?.type === 'revealUp' ||
      animations?.title?.type === 'revealUp' ||
      titleLg
      ? 'full-width'
      : 'reduced-width';
  };

  const hasTextContent = () => {
    if (title || titleLg || subtitle || text || textLg || cta) {
      return true;
    }
    return false;
  }
  
  return (
    <ComponentContainer
      width={width}
      marginBottom={marginBottom}
      marginBottomOnMobile={marginBottomOnMobile}
      marginTopPage={marginTopPage}
      isProject={isProject}
      scrollSpeed={
        typeof scrollSpeed === 'string'
          ? scrollSpeed === 'normal'
            ? undefined
            : scrollSpeed === 'slow'
              ? 0.6
              : undefined
          : scrollSpeed
      }
      animationContainer={animationContainer}
      scrollAnimation={scrollAnimation}
      className={className}
    >
      <TwoColContainer
        borderTop={borderTop as boolean}
        paddingTop="inner"
        sideTitle={sideText}
        addColIfNoSideText={true}
        colorSideText={colorSideText}
        sideTextTag={sideTextTag}
        className={`${hasTextContent() ? 'has-text-content' : 'no-content'}`}
      >
        <Styled.MainText
          isContentTwoColumns={isContentTwoColumns}
          className={
            getTextContainerWidth() ? 'has-trimmed-text' : 'no-trimmed-text'
          }
        >
          <Typography
            text={titleLg}
            size="4xl"
            fontFamily="helvetica-light"
            className="titleLg"
            animation={{
              type: animations?.titleLg?.type || 'none',
            }}
            isInTwoColumns={isContentTwoColumns}
            {...(sizeTitleMobile && { sizeMobile: sizeTitleMobile })}

          />
          <Typography
            text={title}
            size={isContentTwoColumns ? '2xl' : '3xl'}
            fontFamily="helvetica-light"
            className="title"
            animation={animations?.title}
            isInTwoColumns={isContentTwoColumns}
            sizeMobile={sizeTitleMobile || '4xl'}
          />
          <Typography
            text={subtitle}
            size={isContentTwoColumns ? 'lg' : '2xl'}
            fontFamily="helvetica-light"
            className="subtitle"
            animation={animations?.subtitle}
            isInTwoColumns={isContentTwoColumns}
            sizeMobile={sizeSubtitleMobile || '4xl'}
          />
          <Typography
            text={textLg}
            size="lg"
            fontFamily="helvetica-regular"
            className="text-lg"
            animation={animations?.textLg}
            lineHeight="lg"
          />
          <Typography
            text={text}
            size="base"
            fontFamily="helvetica-regular"
            animation={animations?.text}
            lineHeight="lg"
            className="text-base"
          />
          {cta && (
            <CustomLink
              text={cta.text}
              href={cta.href}
              color="primary"
              size="lg"
              // className={animation === 'revealUp' ? 'animated-link' : ''}
            />
          )}
          {projectsCounter &&
            Array.isArray(projectsCounter.counters) &&
            projectsCounter.counters.length > 0 && (
              <Styled.CounterCountainer>
                <ProjectsCounter counters={projectsCounter.counters} />
              </Styled.CounterCountainer>
            )}
        </Styled.MainText>
      </TwoColContainer>
    </ComponentContainer>
  );
};
